import React from 'react';
import {
  DefaultZIndexes,
  ZIndexLayer,
  useActiveTooltipDataPoints,
  useIsTooltipActive,
  usePlotArea,
  useYAxisScale,
} from 'recharts';

/**
 * The tooltip cursor's look, shared by the vertical line (pass it as the
 * Tooltip's `cursor`) and the horizontal one below, so the two always match.
 * These are Recharts' own defaults for the vertical cursor, pinned here so a
 * future default change cannot pull the pair apart.
 */
export const CROSSHAIR_STROKE = { stroke: '#ccc', strokeWidth: 1 } as const;

// Recharts types ZIndexLayer as returning ReactNode, which TypeScript 4.9 (pinned
// for the Vercel build) does not accept as a JSX element type. Same component,
// narrower return type.
const Layer = ZIndexLayer as unknown as React.FC<{ zIndex: number; children: React.ReactNode }>;

interface Props {
  /** Key of the plotted value in each data row (the Area / Line `dataKey`). */
  dataKey: string;
  /** Text for the axis pill, e.g. formatCompactMoney(v, code). */
  format: (value: number) => string;
  /** Match the Y-axis tick font size so the pill reads as part of the axis. */
  fontSize?: number;
}

/**
 * Horizontal half of the crosshair: a line across the plot at the hovered data
 * point's VALUE, plus a pill on the Y-axis naming it.
 *
 * Driven entirely by the Tooltip's own state rather than chart mouse handlers:
 * the hovered row comes from `useActiveTooltipDataPoints`, so it snaps to the
 * same data point the vertical cursor and the active dot snap to, follows touch
 * exactly like they do, and disappears when they do. No React state, so moving
 * the cursor re-renders this one small component and nothing else.
 *
 * Must be rendered as a child of the chart (it reads the chart's context).
 */
export function HorizontalCrosshair({ dataKey, format, fontSize = 11 }: Props) {
  const active = useIsTooltipActive();
  const points = useActiveTooltipDataPoints<Record<string, unknown>>();
  const yScale = useYAxisScale();
  const plot = usePlotArea();

  const value = points?.[0]?.[dataKey];
  if (!active || typeof value !== 'number' || !isFinite(value) || !yScale || !plot) return null;

  const y = yScale(value);
  if (typeof y !== 'number' || !isFinite(y)) return null;
  // Outside the visible domain: nothing sensible to draw.
  if (y < plot.y || y > plot.y + plot.height) return null;

  // The pill sits in the Y-axis gutter, right-aligned against the plot like the
  // tick labels, and is opaque so it cleanly covers the tick it lands on rather
  // than overprinting it. Width is estimated from the text (no DOM measuring, so
  // no layout read per mouse move). Where the gutter is narrower than the pill
  // (the mobile axis), it starts at the left edge and overlaps the plot slightly
  // instead of being clipped by the SVG.
  const text = format(value);
  const padX = 4;
  const h = fontSize + 7;
  const w = Math.ceil(text.length * fontSize * 0.58) + padX * 2;
  const right = plot.x - 1;
  const left = Math.max(1, right - w);
  // Never above the SVG's top edge (the only side it could clip: the X axis sits
  // below the plot, so there is always room underneath).
  const top = Math.max(0, y - h / 2);

  return (
    <Layer zIndex={DefaultZIndexes.cursorLine}>
      <g pointerEvents="none" className="recharts-horizontal-crosshair">
        <line x1={plot.x} x2={plot.x + plot.width} y1={y} y2={y} {...CROSSHAIR_STROKE} />
        {/* Same surface, border and text tokens as the chart tooltips, so it is
            readable in both themes. */}
        <rect
          x={left}
          y={top}
          width={w}
          height={h}
          rx={h / 2}
          fill="rgb(var(--sl-800))"
          stroke="rgb(var(--sl-700))"
          strokeWidth={1}
        />
        <text
          x={left + w / 2}
          y={top + h / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight={600}
          fill="rgb(var(--sl-100))"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {text}
        </text>
      </g>
    </Layer>
  );
}
